import React from 'react'
import { useReactiveVar } from '@apollo/client'
import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { Globe } from 'react-feather'
import { projectVar } from '../../apollo/reactive-vars'
import { useProjects } from '../../project/use-projects'
import {
  HourSeriesCollectionType,
  HourSeriesType,
  ProjectVersionFragment,
  ProjectVisibility,
  useGlobalHourSeriesCollectionsQuery,
} from '../../generated/graphql'
import { SingleSettingSection } from '../common/SingleSettingSection'
import { YearPicker } from '../form/YearPicker'
import { useAuthorization } from '../../authorization/use-authorization'
import { useProfile } from '../profile/ProfileProvider'
import {
  createFilterOutAutogeneratedHourSeriesFromTemplate,
  isValidAutogeneratedForTemperaturesCollection,
  sortHourSeriesCollections,
} from '../../hour-series-collection/hour-series-collection-utility'
import { useComparisonCalculator } from '../../calculators/use-comparison-calculator'
import { ProjectVersionPicker } from '../form/ProjectVersionPicker'
import { useProjectVersions } from '../../project-versions/use-project-versions'
import { Checkbox } from '../form/Checkbox'
import { ProjectTemperatureCurves } from './ProjectTemperatureCurves'

export function ProjectSettingsPage(): React.ReactElement {
  const { updateProject } = useProjects()
  const { updateProjectVersion } = useProjectVersions()
  const profile = useProfile()
  const project = useReactiveVar(projectVar)
  const { t } = useTranslation()
  const canEditProject = useAuthorization().canEditProject(project)
  const { data, loading } = useGlobalHourSeriesCollectionsQuery()
  const { updateComparisonCalculation } = useComparisonCalculator()

  if (!project) {
    return <div />
  }

  const resolveUserGroupValue = () => {
    if (project.visibility === ProjectVisibility.UserGroup) {
      return project.userGroup?.id
    }

    return project.visibility
  }

  const onUserGroupChange = (
    visibilityOrUserGroupId: ProjectVisibility | string,
  ) => {
    const visibility = visibilityOrUserGroupId as ProjectVisibility
    if (ProjectVisibility[visibility]) {
      void updateProject(project.id, { visibility, userGroupId: null })
      return
    }

    void updateProject(project.id, {
      visibility: ProjectVisibility.UserGroup,
      userGroupId: visibilityOrUserGroupId,
    })
  }

  const autogeneratedCollections = project.hourSeriesCollections.filter(
    isValidAutogeneratedForTemperaturesCollection,
  )

  const filteredTemplates =
    data?.globalHourSeriesCollections
      .filter((collection) =>
        collection.hourSeries.some(
          (hourSeries) => hourSeries.type === HourSeriesType.OutdoorTemperature,
        ),
      )
      .map(
        createFilterOutAutogeneratedHourSeriesFromTemplate(
          autogeneratedCollections,
        ),
      ) ?? []

  const collections = [
    ...project.hourSeriesCollections,
    ...filteredTemplates,
  ].sort(sortHourSeriesCollections)

  const onTemperatureHourSeriesChange = async (value: string) => {
    await updateProject(project.id, {
      temperatureHourSeriesId: value !== 'none' ? value : null,
    })
    updateComparisonCalculation()
  }

  const updateDefaultVersion = async (newDefaultVersionId: string) => {
    const currentDefaultVersion: ProjectVersionFragment | undefined =
      project.versions.find((version) => version.isDefault)
    const promises = []
    const updateNewPromise = updateProjectVersion(newDefaultVersionId, {
      isDefault: true,
    })
    promises.push(updateNewPromise)
    if (currentDefaultVersion) {
      const updateCurrentPromise = updateProjectVersion(
        currentDefaultVersion.id,
        { isDefault: false },
      )
      promises.push(updateCurrentPromise)
    }
    await Promise.all(promises)
  }
  return (
    <Box>
      <SingleSettingSection
        title={t('projectSettings.userGroup')}
        subtitle={t('projectSettings.userGroupDescription')}
      >
        <TextField
          value={resolveUserGroupValue()}
          onChange={(event) => onUserGroupChange(event.target.value)}
          select
          fullWidth
          disabled={!canEditProject}
        >
          <MenuItem value={ProjectVisibility.AllUsers}>
            {t('projectVisibility.AllUsers')}
          </MenuItem>
          <MenuItem value={ProjectVisibility.Self}>
            {t('projectVisibility.Self')}
          </MenuItem>
          {profile.groups?.map((group) => (
            <MenuItem key={group.id} value={group.id}>
              {group.name}
            </MenuItem>
          ))}
        </TextField>
      </SingleSettingSection>

      <Divider />

      <SingleSettingSection title={t('projectSettings.temperatureHourSeries')}>
        <TextField
          value={project.temperatureHourSeries?.id ?? 'none'}
          onChange={(event) =>
            onTemperatureHourSeriesChange(event.target.value)
          }
          select
          fullWidth
          disabled={!canEditProject || loading}
        >
          <MenuItem value="none">
            <ListItemText>-</ListItemText>
          </MenuItem>
          {collections.map((collection) =>
            collection.hourSeries
              .filter(
                (hourSeries) =>
                  hourSeries.type === HourSeriesType.OutdoorTemperature,
              )
              .map((hourSeries) => (
                <MenuItem key={hourSeries.id} value={hourSeries.id}>
                  {collection.type ===
                  HourSeriesCollectionType.ProjectSpecific ? (
                    <ListItemText>
                      {collection.name} &rsaquo; {hourSeries.name}
                    </ListItemText>
                  ) : (
                    <Box display="flex" alignItems="center">
                      <ListItemIcon>
                        <Globe />
                      </ListItemIcon>
                      <ListItemText>{hourSeries.name}</ListItemText>
                    </Box>
                  )}
                </MenuItem>
              )),
          )}
        </TextField>
      </SingleSettingSection>

      <Divider />

      <SingleSettingSection
        title={t('projectSettings.calendarYear')}
        subtitle={t('projectSettings.calendarYearDescription')}
      >
        <YearPicker
          value={Number(project.calendarYear)}
          onChange={(year) =>
            updateProject(project.id, {
              calendarYear: year ? Number(year) : null,
            })
          }
          disabled={!canEditProject}
        />
      </SingleSettingSection>

      <Divider />

      <SingleSettingSection title={t('projectSettings.defaultVersion')}>
        <ProjectVersionPicker
          versions={project.versions}
          value={
            project.versions.find((version) => version.isDefault)?.id ?? ''
          }
          onChange={(newDefaultVersionId) =>
            updateDefaultVersion(newDefaultVersionId)
          }
          disabled={!canEditProject}
        />
      </SingleSettingSection>

      <Divider />

      <SingleSettingSection title={t('projectSettings.licensePaid')}>
        <Checkbox
          checked={Boolean(project.licensePaid)}
          onChange={(_, newValue) =>
            updateProject(project.id, { licensePaid: Boolean(newValue) })
          }
          disabled={!canEditProject}
        />
      </SingleSettingSection>

      <Divider />

      <SingleSettingSection title={t('projectSettings.temperatureCurves')}>
        <ProjectTemperatureCurves projectId={project.id} />
      </SingleSettingSection>
    </Box>
  )
}
